import { SwipeableDrawer, Card, CardHeader, CardContent, Grid, Box, Button, useMediaQuery, Tooltip, ListItem, List, Divider, ListItemText, ListItemIcon, Typography } from "@mui/material";
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { FC, useState } from "react";
import ReadonlyField from "~/components/input/ReadonlyField";
import { DataAccessTokenDto } from "~/gen/api/encrypt";
import { autoCopyTextFieldProps } from "~/utils/clipboard";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import { useRecordContext } from "ra-core";
import { RecPatch } from "~/dataProvider/tools";



export interface DataAccessTokenDetailProps {
  open: boolean;
  setOpen: (x:boolean) => void;
}

const DataAccessTokenDetail: FC<DataAccessTokenDetailProps> = ({
  open,
  setOpen,
}) => {
  const handleClose = (): void => {
    setOpen(false);
  };

  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const now = new Date().toISOString()
  const tokenDto : DataAccessTokenDto&RecPatch = useRecordContext()
  const expirationReached = !!tokenDto && new Date(tokenDto.expirationTime || 0).toISOString() < now
  const invalidated = !!tokenDto &&
    (tokenDto.keyInvalidated || expirationReached)
  return (
    <SwipeableDrawer
      anchor="right"
      onOpen={() => {}}
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      {tokenDto &&
      
        <Card sx={{ overflowY: 'auto', mb: 4, }} >
          <CardHeader title="Data Access Token Info" />
          <CardContent>
            <Grid container spacing={4} >
              <DataAccessTokenDetailInternal tokenDto={tokenDto} expirationReached={expirationReached} invalidated={invalidated} />
            </Grid>
          </CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2
            }}
          >
            <Button
              color="primary"
              variant="outlined"
              onClick={handleClose}
            >
              Close
            </Button>
          </Box>
        </Card>
      }

    </SwipeableDrawer>
  )
}

export default DataAccessTokenDetail;


export const DataAccessTokenDetailInternal : FC<{tokenDto:DataAccessTokenDto, expirationReached:boolean, invalidated:boolean}> = ({
  tokenDto,
  expirationReached,
  invalidated,
}) => {
  const format = useUserTimeFormat()
  const [copied, setCopied] = useState(false)

  return (
    <>
      <Grid item xs={12} >
        <List sx={{ '& li.MuiListItem-root.sub div.MuiListItemText-root': { textAlign: 'end' } }}>
          <ListItem>
            <ListItemIcon>
              <ConfirmationNumberIcon color={invalidated ? "error" : "success"} />
            </ListItemIcon>
            <ListItemText 
              sx={{ justifyContent: 'space-between' }}
              primary={
                <>
                  <b>{tokenDto.tokenName}</b>
                  {!expirationReached && invalidated &&
                    <Typography variant="caption" color="GrayText" sx={{ float: 'right' }}>Key Retired</Typography>
                  }
                </>
              } 
            />
          </ListItem>
          <Divider />
          <ListItem className="sub">
            <ListItemIcon>
              <Typography variant="caption" color="InactiveCaptionText">
                Target Database
              </Typography>
            </ListItemIcon>
            <ListItemText primary={tokenDto.databaseName} />
          </ListItem>
          <Divider />
          <ListItem className="sub">
            <ListItemIcon>
              <Typography variant="caption" color="InactiveCaptionText">
                Lifetime
              </Typography>
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography variant="body2" >
                  {format(tokenDto.createdTime || 0, 'Pp')}{" - "}
                  {format(tokenDto.expirationTime || 0, 'Pp')}
                </Typography>
              } 
            />
          </ListItem>
          <Divider />
          <ListItem className="sub">
            <ListItemIcon>
              <Typography variant="caption" color="InactiveCaptionText">
                Requested By
              </Typography>
            </ListItemIcon>
            <ListItemText primary={tokenDto.requestUserName} />
          </ListItem>
          <Divider />
          <ListItem className="sub">
            <ListItemIcon>
              <Typography variant="caption" color="InactiveCaptionText">
                Specified Consumer
              </Typography>
            </ListItemIcon>
            <ListItemText primary={tokenDto.consumerUserName} />
          </ListItem>
          <Divider />
          <ListItem className="sub">
            <ListItemIcon>
              <Typography variant="caption" color="InactiveCaptionText">
                Data Access Roles
              </Typography>
            </ListItemIcon>
            <ListItemText primary={(tokenDto.dataAccessRoles || '').split(',').join(', ')} />
          </ListItem>
          <Divider />
        </List>
      </Grid>
      <Grid item xs={12}>
        {tokenDto.token &&
          <Tooltip title={copied ? 'Copied' : 'Click to Copy'}>
            <Box>
              <ReadonlyField 
                id="data-access-token-generated"
                label="Token Content"
                value={tokenDto.token || ''}
                {...autoCopyTextFieldProps(
                  () => setCopied(true), 
                  () => setTimeout(() => setCopied(false), 500)
                )}
                {...{ inputProps: { tabIndex: 0 }}}
              />
            </Box>
          </Tooltip>}
      </Grid>
    </>
  )
}
